// src/api/services/roleService.ts
import apiClient from '@/api/apiClient';
import { AxiosResponse } from 'axios';

export interface Role {
    id: number;
    code: string;
    name: string;
    description: string;
}

export interface StrapiResponse<T> {
    data: {
        id: number;
        attributes: Omit<T, 'id'>
    }[];
    meta: {
        pagination: {
            page: number;
            pageSize: number;
            pageCount: number;
            total: number;
        }
    }
}

/**
 * Service quản lý các thao tác liên quan đến Role/RoleFunction
 */
class RoleService {
    /**
     * Lấy danh sách tất cả các vai trò
     */
    async getAllRoles(): Promise<Role[]> {
        try {
            const response: AxiosResponse<StrapiResponse<Role>> = await apiClient.get('/api/role-functions');

            return response.data.data.map(item => ({
                id: item.id,
                code: item.code,
                name: item.name,
                description: item.description
            }));
        } catch (error) {
            throw error;
        }
    }

    /**
     * Lấy danh sách vai trò theo mảng code
     */
    async getRolesByCodes(codes: string[]): Promise<Role[]> {
        if (!codes.length) return [];

        try {
            const filters = codes.map(code => `filters[code][$in]=${code}`).join('&');
            const response: AxiosResponse<StrapiResponse<Role>> = await apiClient.get(`/api/role-functions?${filters}`);

            return response.data.data.map(item => ({
                id: item.id,
                code: item.code,
                name: item.name,
                description: item.description
            }));
        } catch (error) {
            throw error;
        }
    }

    /**
     * Lấy thông tin chi tiết của một vai trò
     */
    async getRoleByCode(code: string): Promise<Role | null> {
        try {
            const response: AxiosResponse<StrapiResponse<Role>> = await apiClient.get(`/api/role-functions?filters[code][$eq]=${code}`);

            if (response.data.data.length === 0) {
                return null;
            }

            const item = response.data.data[0];
            return {
                id: item.id,
                code: item.code,
                name: item.name,
                description: item.description
            };
        } catch (error) {
            throw error;
        }
    }
}

export default new RoleService();